// Iter 62ba — Modal simple pour créer une tâche Elevate directement depuis ASA
// (founders only). Champs demandés par Hugo : titre, description, date, owner.
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { createElevateTask } from '../hooks/useElevate';
import { X, Zap, Loader2 } from './icons';

export default function CreateElevateTaskModal({ onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [owner, setOwner] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    const clean = title.trim();
    if (!clean) {
      toast.error('Un titre est requis');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: clean,
        description: description.trim() || undefined,
        due_date: dueDate || undefined,
        owner: owner.trim() || undefined,
      };
      const r = await createElevateTask(payload);
      if (r?.remote_synced) {
        toast.success('Tâche envoyée à Elevate School ✓');
      } else {
        toast.success('Tâche enregistrée localement · sync Elevate en attente', { duration: 5000 });
      }
      if (navigator.vibrate) navigator.vibrate(15);
      onCreated?.(r?.task || r?.pending_local?.payload);
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail?.message || 'Erreur Elevate') : (detail || 'Erreur Elevate');
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return createPortal((
    <div
      data-testid="create-elevate-modal"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 400,
        background: 'rgba(4,8,20,0.72)', backdropFilter: 'blur(12px)',
        display: 'grid', placeItems: 'center', padding: 20,
        width: '100vw', height: '100vh',
      }}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 520, borderRadius: 20, overflow: 'hidden',
          background:
            'radial-gradient(120% 100% at 0% 0%, rgba(91,190,252,0.14) 0%, rgba(91,190,252,0) 55%),'
            + ' linear-gradient(160deg, rgba(30,40,84,0.98) 0%, rgba(14,20,50,0.99) 100%)',
          border: '1.5px solid rgba(91,190,252,0.35)',
          boxShadow: '0 40px 80px -20px rgba(0,0,0,0.75), inset 0 1px 0 rgba(255,255,255,0.06)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '18px 20px 14px',
          borderBottom: '1px solid rgba(91,190,252,0.18)',
        }}>
          <span aria-hidden style={{
            width: 34, height: 34, borderRadius: 10,
            display: 'grid', placeItems: 'center',
            background: 'linear-gradient(140deg, rgba(91,190,252,0.35), rgba(91,190,252,0.10))',
            border: '1px solid rgba(91,190,252,0.55)',
            color: '#5BBEFC',
          }}>
            <Zap size={16} strokeWidth={2.2} />
          </span>
          <div style={{ flex: 1 }}>
            <div style={{
              fontFamily: 'Sora', fontWeight: 800, fontSize: 9.5,
              color: '#5BBEFC', letterSpacing: '0.16em', textTransform: 'uppercase',
            }}>Elevate Influence School</div>
            <div style={{
              fontFamily: 'Sora', fontWeight: 800, fontSize: 15,
              color: 'var(--t1)', marginTop: 2,
            }}>Nouvelle tâche Elevate</div>
          </div>
          <button
            type="button"
            data-testid="create-elevate-close"
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--t3)', padding: 6, display: 'grid', placeItems: 'center',
            }}
          ><X size={18} /></button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Titre" required>
            <input
              data-testid="create-elevate-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex. Livrer le kit onboarding pour Jibia"
              autoFocus
              style={inputStyle}
            />
          </Field>

          <Field label="Description">
            <textarea
              data-testid="create-elevate-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Détails, contexte, lien Drive…"
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 78, fontFamily: 'inherit' }}
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Date">
              <input
                data-testid="create-elevate-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="Owner (email)">
              <input
                data-testid="create-elevate-owner"
                type="email"
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                placeholder="prenom@elevateinfluence.com"
                style={inputStyle}
              />
            </Field>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 20px 20px',
          display: 'flex', gap: 10, justifyContent: 'flex-end',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          background: 'rgba(4,8,20,0.35)',
        }}>
          <button
            type="button"
            data-testid="create-elevate-cancel"
            onClick={onClose}
            style={{
              padding: '10px 16px', borderRadius: 10,
              background: 'transparent', border: '1px solid rgba(255,255,255,0.14)',
              color: 'var(--t2)', fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
              cursor: 'pointer',
            }}
          >Annuler</button>
          <button
            type="submit"
            data-testid="create-elevate-submit"
            disabled={busy || !title.trim()}
            style={{
              padding: '10px 18px', borderRadius: 10,
              background: busy || !title.trim()
                ? 'rgba(91,190,252,0.25)'
                : 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)',
              border: 'none',
              color: '#0B1330', fontFamily: 'Sora', fontWeight: 800, fontSize: 12.5,
              cursor: busy || !title.trim() ? 'not-allowed' : 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 8,
              boxShadow: '0 8px 24px -6px rgba(91,190,252,0.55)',
            }}
          >
            {busy ? <Loader2 size={13} className="spin" /> : <Zap size={13} strokeWidth={2.4} />}
            {busy ? 'Envoi vers Elevate…' : 'Créer dans Elevate'}
          </button>
        </div>
      </form>
    </div>
  ), document.body);
}

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.12)',
  color: 'var(--t1)',
  fontFamily: 'Sora', fontWeight: 500, fontSize: 13,
  outline: 'none',
};

function Field({ label, required, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{
        fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
        color: 'var(--t3)', letterSpacing: '0.08em', textTransform: 'uppercase',
      }}>
        {label}{required && <span style={{ color: '#FF6B6B', marginLeft: 4 }}>*</span>}
      </span>
      {children}
    </label>
  );
}
